import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import BackdropOverlay from "../common/BackdropOverlay.jsx";
import {
    DIALOG_ACCEPT_BUTTON_SX,
    DIALOG_CANCEL_BUTTON_SX,
    DIALOG_PAPER_RADIUS,
} from "../common/dialogActionButtonSx";
import { colors } from "../../theme/colors";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const MOBILE_MQ = `@media ${MOBILE_NAV_MEDIA}`;

export default function SmallerMatchOfferDialog({
    open,
    matchLabel,
    onAccept,
    onContinue5v5,
}) {
    if (!open) return null;

    return (
        <BackdropOverlay open>
            <Box
                role="dialog"
                aria-modal="true"
                aria-labelledby="smaller-match-offer-title"
                aria-describedby="smaller-match-offer-description"
                sx={{
                    position: "relative",
                    width: { xs: "100%", sm: "25.5rem", xl: "26.5rem" },
                    maxWidth: "38.5rem",
                    overflow: "hidden",
                    borderRadius: DIALOG_PAPER_RADIUS,
                    border: `1px solid ${colors.border.default}`,
                    background:
                        "linear-gradient(165deg, rgba(18,14,14,0.98) 0%, rgba(10,10,12,0.99) 48%, rgba(8,8,10,1) 100%)",
                    boxShadow: "0 28px 64px rgba(0,0,0,0.55)",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "12%",
                        right: "12%",
                        height: "1px",
                        background:
                            "linear-gradient(90deg, transparent, rgba(232,163,61,0.55), transparent)",
                        pointerEvents: "none",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        px: { xs: "1.35rem", sm: "1.6rem" },
                        pt: "1.3rem",
                        pb: "1.35rem",
                        textAlign: "left",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.45rem",
                            width: "100%",
                            mb: "1rem",
                        }}
                    >
                        <Box
                            aria-hidden
                            sx={{
                                width: "0.38rem",
                                height: "0.38rem",
                                transform: "rotate(45deg)",
                                background: colors.primary.main,
                                boxShadow: `0 0 10px ${alpha(colors.primary.main, 0.5)}`,
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: "0.59rem",
                                fontWeight: 800,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: alpha(colors.text.secondary, 0.78),
                            }}
                        >
                            Alternate format
                        </Typography>
                        <Box
                            aria-hidden
                            sx={{
                                flex: 1,
                                height: "1px",
                                background: alpha("#fff", 0.07),
                            }}
                        />
                        <Typography
                            component="span"
                            sx={{
                                px: "0.55rem",
                                py: "0.2rem",
                                borderRadius: "5px",
                                border: `1px solid ${alpha(colors.primary.main, 0.28)}`,
                                background: alpha(colors.primary.main, 0.08),
                                color: colors.primary.main,
                                fontSize: "0.62rem",
                                fontWeight: 850,
                                letterSpacing: "0.1em",
                                lineHeight: 1.2,
                            }}
                        >
                            {matchLabel}
                        </Typography>
                    </Box>

                    <Typography
                        id="smaller-match-offer-title"
                        component="h2"
                        sx={{
                            m: 0,
                            fontSize: { xs: "1.3rem", xl: "1.5rem" },
                            fontWeight: 750,
                            letterSpacing: "0.015em",
                            color: colors.text.primary,
                        }}
                    >
                        A {matchLabel} match is ready
                    </Typography>
                    <Typography
                        id="smaller-match-offer-description"
                        sx={{
                            mt: "0.45rem",
                            maxWidth: "none",
                            fontSize: { xs: "0.8rem", xl: "0.86rem" },
                            lineHeight: 1.45,
                            color: alpha(colors.text.secondary, 0.88),
                        }}
                    >
                        Not enough players are available for 5v5 yet. Start sooner
                        with a balanced {matchLabel} under the same matchmaking rules.
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "0.75rem",
                            mt: "1.2rem",
                            pt: "1.05rem",
                            width: "100%",
                            maxWidth: "none",
                            borderTop: `1px solid ${alpha("#fff", 0.06)}`,
                            [MOBILE_MQ]: {
                                flexDirection: "column",
                                maxWidth: "100%",
                            },
                        }}
                    >
                        <Button
                            type="button"
                            onClick={onContinue5v5}
                            sx={{
                                ...DIALOG_CANCEL_BUTTON_SX,
                                flex: { xs: "none", sm: 1 },
                                minWidth: { sm: "9.5rem" },
                                [MOBILE_MQ]: { width: "100%", minWidth: 0 },
                            }}
                        >
                            Continue 5v5
                        </Button>
                        <Button
                            type="button"
                            disableElevation
                            onClick={onAccept}
                            sx={{
                                ...DIALOG_ACCEPT_BUTTON_SX,
                                flex: { xs: "none", sm: 1 },
                                minWidth: { sm: "9.5rem" },
                                height: "2.6rem",
                                fontSize: "0.72rem",
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                [MOBILE_MQ]: { width: "100%", minWidth: 0 },
                            }}
                        >
                            Accept {matchLabel}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </BackdropOverlay>
    );
}

SmallerMatchOfferDialog.propTypes = {
    open: PropTypes.bool,
    matchLabel: PropTypes.string,
    onAccept: PropTypes.func,
    onContinue5v5: PropTypes.func,
};
